/**
 * swederm — Cart Discount + Gift Card Handler (Krok 2 — v6)
 * Plik: assets/cart-discount-swederm.js
 *
 * Obsługuje JEDNO pole, w które użytkownik może wpisać:
 *   • kod rabatowy (discount code) — aplikowany przez /cart/update.js
 *   • numer karty podarunkowej (gift card) — zapisywany w sessionStorage
 *     i doklejany do URL checkoutu przy kliknięciu "Do kasy"
 *
 * Rozpoznawanie typu:
 *   • Shopify gift cards mają standardowo 16 znaków alfanumerycznych
 *     (bez 0,1,O,I — ale my nie polegamy na tym, bo użytkownik mógł wpisać
 *     z myślnikami/spacjami). Po stripie separatorów: 16+ znaków
 *     alfanumerycznych → gift card.
 *
 * UWAGA dla admina sklepu:
 *   Nie twórz kodów rabatowych z 16+ ciągłymi znakami alfanumerycznymi,
 *   bo zostaną błędnie rozpoznane jako gift card. Bezpiecznie: używaj
 *   myślników/podkreśleń (np. WELCOME-2026) lub krótszych nazw.
 *
 * V6: dodana obsługa gift cards (sessionStorage + URL injection do checkout).
 */

(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const GIFT_CARD_STORAGE_KEY = 'sw_gift_cards';
  const GIFT_CARD_MIN_LENGTH = 16;

  // ---------- Helpers: gift card detection & masking ----------

  /**
   * Strip wszystkich separatorów (myślniki, spacje, podkreślenia) i upper-case.
   */
  function normalizeCode(raw) {
    return String(raw || '').replace(/[\s\-_]+/g, '').toUpperCase();
  }

  /**
   * Czy ciąg wygląda jak gift card?
   * Kryterium: po normalizacji 16+ znaków, wyłącznie A-Z i 0-9.
   */
  function looksLikeGiftCard(raw) {
    const norm = normalizeCode(raw);
    if (norm.length < GIFT_CARD_MIN_LENGTH) return false;
    return /^[A-Z0-9]+$/.test(norm);
  }

  /**
   * Zwraca zamaskowaną postać numeru gift card do wyświetlenia w chipie.
   * Pokazuje pierwsze 4 i ostatnie 4 znaki, środek jako •••.
   * Przykład: "ABCD-EFGH-IJKL-MNOP" → "ABCD••••MNOP"
   */
  function maskGiftCard(code) {
    const norm = normalizeCode(code);
    if (norm.length <= 8) return norm;
    return `${norm.slice(0, 4)}••••${norm.slice(-4)}`;
  }

  // ---------- Gift card storage (sessionStorage) ----------

  function getStoredGiftCards() {
    try {
      const raw = sessionStorage.getItem(GIFT_CARD_STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(c => typeof c === 'string') : [];
    } catch {
      return [];
    }
  }

  function setStoredGiftCards(cards) {
    try {
      sessionStorage.setItem(GIFT_CARD_STORAGE_KEY, JSON.stringify(cards));
    } catch (err) {
      console.warn('[swederm-discount] sessionStorage write failed', err);
    }
  }

  function addStoredGiftCard(code) {
    const norm = normalizeCode(code);
    const existing = getStoredGiftCards();
    if (existing.some(c => normalizeCode(c) === norm)) return;
    existing.push(norm);
    setStoredGiftCards(existing);
  }

  function removeStoredGiftCard(code) {
    const norm = normalizeCode(code);
    const existing = getStoredGiftCards().filter(c => normalizeCode(c) !== norm);
    setStoredGiftCards(existing);
  }

  // ---------- Cart API (discount codes) ----------

  const CartAPI = {
    // async get() {
    //   const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    //   return r.json();
    // },
    async get() {                                                                                                                                                                                  
    // sb_nc + no-store (2026-08-05): /cart.js bywa serwowany z CACHE SERWEROWEGO Shopify                                                                                                        
    // (nagłówek x-cartjs-cache) — pętla „cierpliwości" niżej podejmuje decyzje z tej migawki,                                                                                                   
    // więc każdy odczyt musi omijać cache (ta sama zasada co w silniku appki SALESBOOST).                                                                                                       
    const r = await fetch(`/cart.js?sb_nc=${Date.now()}`, {                                                                                                                                      
      headers: { Accept: 'application/json' },                                                                                                                                                   
      cache: 'no-store',                                                                                                                                                                         
    });                                                                                                                                                                                          
    return r.json();                                                                                                                                                                             
  },  

    async update(payload) {
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await r.json();
      return r.json();
    },
  };

  function getAllActiveDiscounts(cart) {
    const codes = new Set();
    (cart.cart_level_discount_applications || []).forEach(d => {
      const code = d.title || d.code;
      if (code) codes.add(code);
    });
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(alloc => {
        const code = alloc.discount_application?.title;
        if (code) codes.add(code);
      });
      (item.discount_allocations || []).forEach(alloc => {
        const code = alloc.discount_application?.title;
        if (code) codes.add(code);
      });
    });
    return Array.from(codes);
  }


    // 05.08.2026 === Kody do ZACHOWANIA przy nadpisywaniu listy (poprawka 2026-08-05, appka SALESBOOST) ===                                                                                                    
    // Każdy zapis kodów robi /cart/update.js {discount: CSV}, który NADPISUJE pełną listę kodów                                                                                                     
    // w koszyku — a getAllActiveDiscounts widzi wyłącznie kody z NALICZENIAMI. Ważny kod potrafi                                                                                                    
    // chwilowo nie mieć naliczenia (zmierzone 2026-08-05: kod appki z małą zniżką na prezencie                                                                                                      
    // został „przebity" cudzym kodem −30% na tej samej linii → zero naliczeń → każda kolejna                                                                                                        
    // operacja pola po cichu WYMAZYWAŁA go z koszyka, a appka zabierała klientowi prezent).                                                                                                         
    // Dlatego CSV budujemy z sumy TRZECH źródeł:                                                                                                                                                    
    //   naliczenia ∪ wpisy cart.discount_codes z applicable:true ∪ kody ofert appki SALESBOOST                                                                                                      
    // (z configu #sb-aa-config — jest na każdej stronie, gdy app embed appki jest włączony).                                                                                                        
    // Literówki/martwe wpisy nie są żadnym z trzech źródeł → sprzątanie działa jak dotąd.                     
    function getSalesboostOfferCodes() {                                                                                                                                                             
      try {                                                                                                                                                                                          
        const el = document.getElementById('sb-aa-config');                                                                                                                                          
        if (!el) return [];                                                                                                                                                                          
        const cfg = JSON.parse(el.textContent || 'null');                                                                                                                                            
        const offers = (cfg && Array.isArray(cfg.offers)) ? cfg.offers : [];                                                                                                                         
        return offers                                                                                                                                                                                
          .filter(o => o && o.trigger === 'code' && o.code_trigger && o.code_trigger.code)                                                                                                           
          .map(o => String(o.code_trigger.code).toUpperCase());                                                                                                                                      
      } catch {                                                                                                                                                                                      
        return []; // brak/zepsuty config → zachowanie sprzed poprawki (same naliczenia)                                                                                                             
      }                                                                                                                                                                                              
    } 

    function getCodesToPreserve(cart) {                                                                                                                                                              
      const keep = new Map(); // UPPER → pisownia do wysłania (pierwsza napotkana)                                                                                                                   
      getAllActiveDiscounts(cart).forEach(c => {                                                                                                                                                     
        const up = String(c).toUpperCase();                                                                                                                                                          
        if (!keep.has(up)) keep.set(up, c);                                                                                                                                                          
      });                                                                                                                                                                                            
      const sbCodes = getSalesboostOfferCodes();                                                                                                                                                     
      (cart.discount_codes || []).forEach(d => {                                                                                                                                                     
        if (!d || !d.code) return;                                                                                                                                                                   
        const up = String(d.code).toUpperCase();                                                                                                                                                     
        if (keep.has(up)) return;                                                                                                                                                                    
        if (d.applicable === true || sbCodes.indexOf(up) !== -1) keep.set(up, d.code);                                                                                                               
      });                                                                                                                                                                                            
      return Array.from(keep.values());                                                                                                                                                              
    }   




  // ---------- Drawer refresh ----------
  function notifyCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function refreshDrawerSection() {
    try {
      const url = `${window.location.pathname}?sections=cart-drawer`;
      const r = await fetch(url);
      const data = await r.json();
      const html = data['cart-drawer'];
      if (!html) return;

      const drawer = $('#Cart-Drawer');
      if (!drawer) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newInner = doc.querySelector('#Cart-Drawer .side-panel-inner');
      const oldInner = $('.side-panel-inner', drawer);
      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
      }
    } catch (err) {
      console.error('[swederm-discount] refresh drawer', err);
    }
  }

  async function fullRefresh() {
    notifyCartRefresh();
    setTimeout(async () => {
      await refreshDrawerSection();
      // renderGiftCardChips(); // domalowujemy chipy gift card po każdym refreshu
      refreshDynamicChips(); // domalowujemy chipy JS (gift cards + kody bez naliczenia) po każdym refreshu
    }, 100);
  }

  // ---------- Gift card chips rendering ----------

  /**
   * Liquid render nie zna sessionStorage, więc chipy gift card dorysowujemy
   * po stronie JS. Wkładamy je do tej samej listy <ul data-sw-discount-chips>,
   * obok chipów discount renderowanych przez Liquid.
   */
  function renderGiftCardChips() {
    const block = $('[data-sw-discount]');
    if (!block) return;
    const list = $('[data-sw-discount-chips]', block);
    if (!list) return;

    // Usuń stare chipy gift card (te dodane przez JS — oznaczone atrybutem)
    $$('[data-sw-giftcard-chip]', list).forEach(el => el.remove());

    const cards = getStoredGiftCards();
    if (cards.length === 0) {
      // Jeśli lista była ukryta przez Liquid (hidden), a teraz mamy gift cards
      // — odsłaniamy ją tylko jeśli są chipy. Tu nic do roboty.
      return;
    }

    // Lista mogła być z atrybutem hidden (gdy w koszyku nie było rabatów)
    list.removeAttribute('hidden');

    cards.forEach(code => {
      const li = document.createElement('li');
      li.setAttribute('data-sw-giftcard-chip', '');
      li.innerHTML = `
        <span class="sw-chip">
          <span class="sw-chip__dot" aria-hidden="true"></span>
          <span class="sw-chip__label">Karta podarunkowa: ${escapeHtml(maskGiftCard(code))}</span>
          <button type="button"
                  class="sw-chip__remove"
                  data-sw-giftcard-remove="${escapeAttr(code)}"
                  aria-label="Usuń kartę podarunkową">×</button>
        </span>
      `;
      list.appendChild(li);
    });
  }


  /**                                                                                                                                                                                              
   * 08.05.2026 - Chipy dla kodów ZACHOWANYCH bez naliczenia (poprawka 2026-08-05).                                                                                                                             
   * Liquid rysuje chipy wyłącznie z NALICZEŃ — kod przebity cudzym, większym rabatem                                                                                                              
   * (patrz getCodesToPreserve) siedzi w koszyku, ale chipa nie dostaje i klient nie może                                                                                                          
   * go zdjąć ani zobaczyć. Dorysowujemy takie chipy w JS — ten sam wzorzec co gift cards.                                                                                                         
   * Bez kwoty (sw-chip__value), bo kod chwilowo nic nie nalicza. Krzyżyk działa przez                                                                                                             
   * istniejący handler data-sw-discount-remove.                                                                                                                                                   
   */   
  function renderPreservedCodeChips(cart) {                                                                                                                                                        
    const block = $('[data-sw-discount]');                                                                                                                                                         
    if (!block) return;                                                                                                                                                                            
    const list = $('[data-sw-discount-chips]', block);                                                                                                                                             
    if (!list) return;                                                                                                                                                                             
                                                                                                                                                                                                    
    $$('[data-sw-jscode-chip]', list).forEach(el => el.remove());                                                                                                                                  
                                                                                                                                                                                                    
    const rendered = $$('[data-sw-discount-remove]', list)                                                                                                                                         
      .map(el => String(el.dataset.swDiscountRemove || '').toUpperCase());                                                                                                                         
    const missing = getCodesToPreserve(cart)                                                                                                                                                       
      .filter(c => rendered.indexOf(String(c).toUpperCase()) === -1);                                                                                                                              
    if (missing.length === 0) return;                                                                                                                                                              
                                                                                                                                                                                                    
    list.removeAttribute('hidden');                                                                                                                                                                
    missing.forEach(code => {                                                                                                                                                                      
      const li = document.createElement('li');                                                                                                                                                     
      li.setAttribute('data-sw-jscode-chip', '');                                                                                                                                                  
      li.innerHTML = `                                                                                                                                                                             
        <span class="sw-chip">                                                                                                                                                                     
          <span class="sw-chip__dot" aria-hidden="true"></span>                                                                                                                                    
          <span class="sw-chip__label">${escapeHtml(code)}</span>                                                                                                                                  
          <button type="button"                                                                                                                                                                    
                  class="sw-chip__remove"                                                                                                                                                          
                  data-sw-discount-remove="${escapeAttr(code)}"                                                                                                                                    
                  aria-label="Usuń kod ${escapeAttr(code)}">×</button>                                                                                                                             
        </span>                                                                                                                                                                                    
      `;                                                                                                                                                                                           
      list.appendChild(li);                                                                                                                                                                        
    });                                                                                                                                                                                            
  }
  
  /** 08.05.2026/06.08.2026 - Odśwież oba rodzaje chipów dorysowywanych przez JS (gift cards + kody bez naliczenia). */                                                                                                    
  async function refreshDynamicChips() {                                                                                                                                                           
    let cart = null;                                                                                                                                                                               
    try {                                                                                                                                                                                          
      cart = await CartAPI.get();                                                                                                                                                                  
    } catch {                                                                                                                                                                                      
      // best-effort — chipy kodów dorysują się przy następnym odświeżeniu koszyka                                                                                                                 
    }                                                                                                                                                                                              
    // Okno ciszy dla obserwatora drawera (poprawka 2026-08-06): nasze własne zapisy DOM poniżej                                                                                                   
    // też są mutacjami — bez tego okna obserwator wpadłby w pętlę observer→render→observer.                                                                                                       
    chipsSuppressUntil = Date.now() + 300;                                                                                                                                                         
    renderGiftCardChips();                                                                                                                                                                         
    if (cart) renderPreservedCodeChips(cart);                                                                                                                                                      
  } 

  // 06.08.2026---------- Obserwator re-renderów drawera (poprawka 2026-08-06) ----------                                                                                                                    
  // Motyw podmienia treść drawera (innerHTML) z RÓŻNYCH ścieżek: nasze fullRefresh, ATC ze strony                                                                                                 
  // produktu, renderContents silnika appki SALESBOOST. Nasze chipy JS giną wtedy ze starym HTML-em,                                                                                               
  // a zdarzenie cart:refresh dispatchuje TYLKO nasz własny fullRefresh — zmierzone 2026-08-06:                                                                                                    
  // powrót prezentu po dodaniu produktu ZE STRONY PRODUKTU zostawiał kod oferty bez chipa aż do F5                                                                                                
  // (Liquid nie rysuje chipa, bo w chwili renderu kod nie ma jeszcze naliczenia — funkcja rabatowa                                                                                                
  // przelicza się z opóźnieniem). Obserwator odrysowuje chipy po KAŻDEJ podmianie treści drawera —                                                                                                
  // ten sam wzorzec co re-aplikacja klas opcji koszyka w silniku appki (MutationObserver childList).                                                                                              
  let chipsSuppressUntil = 0;                                                                                                                                                                      
  let chipsDebounce = null;                                                                                                                                                                        
  function bindDrawerObserver() {                                                                                                                                                                  
    const drawer = $('#Cart-Drawer');                                                                                                                                                              
    if (!drawer || !window.MutationObserver) return;                                                                                                                                               
    const mo = new MutationObserver(() => {                                                                                                                                                        
      if (Date.now() < chipsSuppressUntil) return;   // to nasze własne rysowanie — nie reagujemy                                                                                                  
      clearTimeout(chipsDebounce);                                                                                                                                                                 
      chipsDebounce = setTimeout(refreshDynamicChips, 200);   // debounce: seria mutacji = jedno odrysowanie                                                                                       
    });                                                                                                                                                                                            
    mo.observe(drawer, { childList: true, subtree: true });                                                                                                                                        
  }    



  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ---------- Apply / remove handlers ----------

  async function applyDiscount(code) {
    if (!code) return;
    const block = $('[data-sw-discount]');
    if (!block) return;
    const errEl = $('[data-sw-discount-error]', block);
    const input = $('[data-sw-discount-input]', block);
    const applyBtn = $('[data-sw-discount-apply]', block);

    if (errEl) errEl.hidden = true;
    if (applyBtn) applyBtn.disabled = true;

    try {
      // GAŁĄŹ A: gift card → sessionStorage + chip, BEZ /cart/update.js
      if (looksLikeGiftCard(code)) {
        addStoredGiftCard(code);
        if (input) input.value = '';
        renderGiftCardChips();
        return; // ważne: nie idziemy w gałąź discount
      }

      // GAŁĄŹ B: discount code (jak dotychczas)
      const cart = await CartAPI.get();
      // const existing = getAllActiveDiscounts(cart);
      // Poprawka 2026-08-05: lista do zachowania = getCodesToPreserve (nie same naliczenia) —                                                                                                     
      // inaczej ważny kod bez chwilowego naliczenia znikałby przy dokładaniu nowego kodu.                                                                                                         
      const existing = getCodesToPreserve(cart); 

      const codeUpper = code.toUpperCase();
      const alreadyApplied = existing.some((c) => c.toUpperCase() === codeUpper);
      const combined = alreadyApplied
        ? existing.join(',')
        : [...existing, code].join(',');

      await CartAPI.update({ discount: combined });




      // const updated = await CartAPI.get();
      // const appliedNow = getAllActiveDiscounts(updated);
      // const ok = appliedNow.some(c => c.toUpperCase() === codeUpper);

      // if (!ok) {
      //   if (errEl) {
      //     errEl.textContent = 'Kod nieprawidłowy, wygasł lub nie spełnia warunków zamówienia.';
      //     errEl.hidden = false;
      //   }
      //   // Koszyk się nie zmienił (kod nie wszedł). Pomijamy fullRefresh, bo
      //   // re-render sekcji podmieniłby .sw-discount__error i komunikat by zniknął.
      //   return;
      // }


      //START - poprawka dla kodu rabatowego, dodającego prezent
      // Weryfikacja z CIERPLIWOŚCIĄ (2026-08-04, appka SALESBOOST — kody dodające prezenty):
      // kod aplikacyjny bywa WAŻNY, ale przez ~1-2 s nie ma jeszcze czego zniżać (appka dopiero
      // dokłada prezent do koszyka) — natychmiastowe sprawdzenie naliczeń dawało fałszywy
      // komunikat „kod nieprawidłowy". UWAGA (pomiar 2026-08-04): cart.discount_codes zapisuje
      // KAŻDY wpisany ciąg, nawet nieistniejący (applicable:false) — sama obecność kodu NICZEGO
      // nie dowodzi. Rozróżniamy więc CZASEM: kod ważny dostaje naliczenie (applicable:true albo
      // wpis w naliczeniach) w ciągu paru sekund; bzdura — nigdy. Po ostatecznej porażce
      // sprzątamy martwy wpis z koszyka (inaczej wisiałby w discount_codes na zawsze).
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const codeSettled = (c) => {
        const inApps = getAllActiveDiscounts(c).some((x) => x.toUpperCase() === codeUpper);
        const entry = (c.discount_codes || []).find(
          (d) => d && String(d.code || '').toUpperCase() === codeUpper
        );
        if (inApps || (entry && entry.applicable === true)) return 'active';
        return entry ? 'pending' : 'absent';
      };

      let updated = await CartAPI.get();
      let state = codeSettled(updated);
      for (const wait of [1200, 1600]) {
        if (state !== 'pending') break;
        await sleep(wait);
        updated = await CartAPI.get();
        state = codeSettled(updated);
      }


      if (state !== 'active') {                                                                                                                                                                    
        // Poprawka 2026-08-05 (runda 2): kod NASZEJ oferty (appka SALESBOOST) nie jest martwy tylko                                                                                               
        // dlatego, że nie nalicza — cudzy większy rabat potrafi przebić zniżkę prezentu OD RAZU                                                                                                   
        // (obcy kod wpisany PRZED naszym), a warunki oferty mogą być spełnione dopiero za chwilę.                                                                                                 
        // Zostawiamy kod w koszyku bez błędu: silnik appki pilnuje prezentu (doda/zdejmie wg                                                                                                      
        // warunków), chip dorysowuje renderPreservedCodeChips. Literówki i cudze martwe kody                                                                                                      
        // nie są na liście ofert → sprzątanie niżej działa dla nich jak dotąd.                                                                                                                    
        if (getSalesboostOfferCodes().indexOf(codeUpper) !== -1) {                                                                                                                                 
          if (input) input.value = '';                                                                                                                                                             
          await fullRefresh();                                                                                                                                                                     
          return;                                                                                                                                                                                  
        }                                                                                                                                                                                          
        if (state === 'pending') { 
          // Martwy wpis (kod zapisany, ale nigdy nie zadziała) — zdejmujemy go z koszyka.
          try {
            const keep = getCodesToPreserve(updated)
              .filter((c2) => c2.toUpperCase() !== codeUpper)
              .join(',');
            await CartAPI.update({ discount: keep });
          } catch (e2) { /* best-effort — komunikat i tak pokazujemy */ }
        }
        if (errEl) {
          errEl.textContent = 'Kod nieprawidłowy, wygasł lub nie spełnia warunków zamówienia.';
          errEl.hidden = false;
        }
        // Koszyk bez zmian dla klienta. Pomijamy fullRefresh, bo re-render sekcji
        // podmieniłby .sw-discount__error i komunikat by zniknął.
        return;
      }
      //END - poprawka dla kodu rabatowego, dodającego prezent


      if (input) input.value = '';
      await fullRefresh();


    } catch (err) {
      console.error('[swederm-discount] applyDiscount', err);
      if (errEl) {
        errEl.textContent = 'Nie udało się zastosować kodu. Spróbuj ponownie.';
        errEl.hidden = false;
      }
    } finally {
      if (applyBtn) applyBtn.disabled = false;
    }
  }

  async function removeDiscount(codeToRemove) {
    try {
      const cart = await CartAPI.get();

      // const remaining = getAllActiveDiscounts(cart).filter(c => c !== codeToRemove);
      // Poprawka 2026-08-05: zachowujemy też ważne kody bez chwilowego naliczenia —                                                                                                               
      // inaczej zdjęcie CUDZEGO kodu wymazywało kod appki i klient tracił prezent.                                                                                                                
      const up = String(codeToRemove || '').toUpperCase();                                                                                                                                         
      const remaining = getCodesToPreserve(cart).filter(c => c.toUpperCase() !== up);

      await CartAPI.update({ discount: remaining.join(',') });
      await fullRefresh();
    } catch (err) {
      console.error('[swederm-discount] removeDiscount', err);
    }
  }

  /**
   * Usunięcie karty podarunkowej.
   *
   * UWAGA na quirk Shopify: jeśli użytkownik wcześniej już odwiedził
   * /checkout z tą kartą (URL ?discount=...), Shopify zapamiętuje ją
   * w sesji checkoutu po stronie serwera. Sam fakt usunięcia chipa
   * (czyszczenie sessionStorage) NIE usuwa karty z tej sesji.
   *
   * Próbujemy dwóch rzeczy, żeby zminimalizować problem:
   *   1) Dummy update koszyka (touch cart note) — czasem wymusza
   *      odświeżenie cart tokena i sesji checkoutu. Brak gwarancji.
   *   2) Pokazujemy użytkownikowi komunikat, że jeśli już był w kasie
   *      z tą kartą, musi ją tam też usunąć ręcznie.
   *
   * Definitywne rozwiązanie wymagałoby Storefront API GraphQL
   * (cartGiftCardCodesRemove, od API 2025-01) i migracji koszyka
   * — to osobny, większy projekt.
   */
  async function removeGiftCard(code) {
    removeStoredGiftCard(code);
    renderGiftCardChips();

    // Próba unstucknięcia sesji checkoutu — touch cart note
    try {
      const cart = await CartAPI.get();
      await CartAPI.update({ note: cart.note || '' });
    } catch (err) {
      // Nie blokujemy UX jeśli się nie uda — to best-effort
      console.warn('[swederm-discount] cart touch after gift card remove failed', err);
    }

    // Komunikat informacyjny — pokazujemy w polu błędu, ale z innym stylem
    const block = $('[data-sw-discount]');
    const errEl = block ? $('[data-sw-discount-error]', block) : null;
    if (errEl) {
      errEl.textContent = 'Karta usunięta. Jeśli była już dodana w kasie, usuń ją również w polu „Kod rabatowy lub karta podarunkowa” w kasie.';
      errEl.hidden = false;
      // Po 8 sekundach chowamy
      setTimeout(() => {
        if (errEl.textContent.startsWith('Karta usunięta')) {
          errEl.hidden = true;
          errEl.textContent = '';
        }
      }, 8000);
    }
  }

  // ---------- Checkout button: inject gift cards into URL ----------

  /**
   * Shopify checkout konsumuje gift cards i discounts z tego samego paramu
   * ?discount=CODE w URL (można też podać wiele oddzielonych przecinkiem,
   * choć Shopify oficjalnie wspiera jeden — wiele jako lista CSV jest
   * "best-effort"). My przekazujemy tylko gift cards; discount codes są
   * już w cart obiekcie po stronie serwera.
   *
   * Hookujemy się w submit formularza /cart, podmieniamy action na
   * /checkout?discount=... — Shopify obsługuje to natywnie.
   */
  function bindCheckoutInjection() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="checkout"]');
      if (!btn) return;

      const cards = getStoredGiftCards();
      if (cards.length === 0) return; // nic do dorzucenia

      const form = btn.closest('form[action^="/cart"], form[action*="/cart"]');
      if (!form) return;

      // Sklejamy wszystkie gift cards CSV-em.
      // (Shopify checkout potrafi przyjąć wiele oddzielonych przecinkami.)
      const discountParam = cards.map(c => normalizeCode(c)).join(',');

      // Zamiast modyfikować action (które normalnie wskazuje /cart i
      // Shopify sam przekierowuje do /checkout), używamy hidden inputa
      // o nazwie "checkout" — to standardowy mechanizm Shopify cart formu —
      // i dorzucamy URL parameter przez nawigację.
      //
      // Najczystsze: ustaw action na /checkout?discount=... i pozwól
      // formowi go submitować. Shopify zaakceptuje.
      try {
        const u = new URL(form.action, window.location.origin);
        u.pathname = '/checkout';
        u.searchParams.set('discount', discountParam);
        form.action = u.pathname + u.search;
      } catch (err) {
        console.warn('[swederm-discount] checkout URL inject failed', err);
      }
    }, true); // capture: chwytamy zanim theme zrobi własną nawigację
  }

  // ---------- Global event listeners ----------

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t) return;

    if (t.closest('[data-sw-discount-apply]')) {
      e.preventDefault();
      const input = $('[data-sw-discount-input]');
      if (input) applyDiscount(input.value.trim());
      return;
    }

    const chipRemove = t.closest('[data-sw-discount-remove]');
    if (chipRemove) {
      e.preventDefault();
      const code = chipRemove.dataset.swDiscountRemove;
      if (code) removeDiscount(code);
      return;
    }

    const giftCardRemove = t.closest('[data-sw-giftcard-remove]');
    if (giftCardRemove) {
      e.preventDefault();
      const code = giftCardRemove.dataset.swGiftcardRemove;
      if (code) removeGiftCard(code);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.target.matches('[data-sw-discount-input]') && e.key === 'Enter') {
      e.preventDefault();
      applyDiscount(e.target.value.trim());
    }
  });

  // Po każdym otwarciu drawera / refresh — odrysuj chipy gift card
  document.addEventListener('cart:refresh', () => {                                                                                                                                                
    setTimeout(refreshDynamicChips, 150);                                                                                                                                                          
  });                                                                                                                                                                                              
                                                                                                                                                                                                     
  // Init                                                                                                                                                                                          
  bindCheckoutInjection();                                                                                                                                                                         
  document.addEventListener('DOMContentLoaded', () => { bindDrawerObserver(); refreshDynamicChips(); });                                                                                           
  if (document.readyState !== 'loading') { bindDrawerObserver(); refreshDynamicChips(); }  
})();
